<div class="row" *ngIf="loading">
  <mat-progress-bar mode="indeterminate" class="custom-loader"></mat-progress-bar>
</div>

<div class="container-fluid mt-3 mb-5">

  <div class="dashboard-card shadow-sm">

    <div class="card-header bg-white border-bottom p-4">
      <div class="row align-items-center">
        <div class="col-md-7">
          <h4 class="font-weight-bold text-dark-purple mb-1">{{ 'generic.team' | translate }}</h4>
          <p class="text-muted mb-4">Manage participants, track responses, and handle invitations.</p>

          <div class="d-flex stats-row">
            <div class="stat-item mr-5">
              <h2 class="mb-0 font-weight-bold text-primary-purple">{{ members?.length || 0 }}</h2>
              <small class="text-uppercase text-muted font-weight-bold tracking-wide">Total Members</small>
            </div>
            <div class="stat-item mr-5">
              <h2 class="mb-0 font-weight-bold text-success">{{ answersSent?.length }}</h2>
              <small class="text-uppercase text-muted font-weight-bold tracking-wide">Responded</small>
            </div>
          </div>
        </div>

        <div class="col-md-5 d-flex justify-content-end" *ngIf="chartOptions">
          <div class="chart-wrapper w-100" style="height: 200px;">
            <div echarts [options]="chartOptions" style="height: 100%; width: 100%;"></div>
          </div>
        </div>
      </div>
    </div>

    <div class="card-body p-0">

      <div class="toolbar p-4 bg-light-purple d-flex flex-column border-bottom">

        <div class="filters-container">

          <div class="search-box">
            <fa-icon [icon]="faSearch" class="search-icon"></fa-icon>
            <input
              type="text"
              class="form-control border-0"
              [placeholder]="'generic.searchByNameOrEmail' | translate"
              [(ngModel)]="filters.searchValue"
              (keyup)="applyFilter()">
          </div>

          <mat-form-field appearance="fill" class="dense-form-field">
            <mat-label>{{ "components.surveys.survey-team.survey-team.filter-by-answer" | translate }}</mat-label>
            <mat-select [(ngModel)]="filters.responseStatus" (selectionChange)="applyFilter()">
              <mat-option value="ALL">{{ "components.decision-table.new-decision-table.select-framework.all" | translate }}</mat-option>
              <mat-option value="SENT">{{ "components.surveys.survey-team.survey-team.filter-by-answered" | translate }}</mat-option>
              <mat-option value="DRAFT">{{ "generic.answers.draft" | translate }}</mat-option>
              <mat-option value="NOT_SENT">{{ "components.surveys.survey-team.survey-team.filter-by-not-answered" | translate }}</mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="fill" class="dense-form-field" *ngIf="emailStatuses?.length > 0">
            <mat-label>{{ "components.surveys.survey-team.survey-team.filter-by-email-status" | translate }}</mat-label>
            <mat-select [(ngModel)]="filters.emailStatus" (selectionChange)="applyFilter()" multiple>
              <mat-option *ngFor="let status of emailStatuses" [value]="status">
                {{ "generic.email-status." + status + ".title" | translate }}
              </mat-option>
            </mat-select>
          </mat-form-field>

        </div>

        <div class="d-flex justify-content-end align-items-center w-100 global-actions">

          <ng-container *ngIf="selectedEmailIds.size > 0">
            <button mat-stroked-button color="primary" class="mr-2" (click)="resendSelectedInvitations()">
              <fa-icon [icon]="faBullhorn" class="mr-2"></fa-icon>
              Resend ({{ selectedEmailIds.size }})
            </button>
            <button mat-stroked-button color="warn" class="mr-2" (click)="deleteSelectedMembers()">
              <fa-icon [icon]="faTrash" class="mr-2"></fa-icon>
              Delete ({{ selectedEmailIds.size }})
            </button>
          </ng-container>

          <button mat-stroked-button class="text-dark-purple border-purple mr-2" (click)="downloadSurveyTeamDataCSV()">
            <fa-icon [icon]="faDownload" class="mr-1"></fa-icon> Export
          </button>

          <button mat-flat-button color="primary"
                  class="bg-primary-purple text-white"
                  *ngIf="!surveyIsClosed && selectedEmailIds.size == 0"
                  (click)="toggleAddNewTeamMemberMode()">
            <fa-icon [icon]="singleTeamMemberToAddActive ? faTimes : faPlusCircle" class="mr-1"></fa-icon>
            {{ singleTeamMemberToAddActive ? ('generic.cancel' | translate) : ('components.decision-table.new-decision-table.compile-proposal.add-team-member' | translate) }}
          </button>

        </div>

      </div>

      <div class="bg-light-purple p-4 border-bottom" *ngIf="singleTeamMemberToAddActive && selectedEmailIds.size == 0">
        <app-single-team-member
          [removable]="false"
          [context]="'survey-team'"
          [addButton]="true"
          (addedTeamMember)="onAddTeamMember($event)"
          [surveyId]="surveyId"
        />
      </div>

      <mat-table [dataSource]="dataSource" matSort class="w-100 custom-table">

        <ng-container matColumnDef="select">
          <mat-header-cell *matHeaderCellDef class="col-select">
            <mat-checkbox (change)="toggleSelectAll($event)"
                          [checked]="isAllSelected()"
                          [indeterminate]="isSomeSelected()"
                          color="primary">
            </mat-checkbox>
          </mat-header-cell>
          <mat-cell *matCellDef="let row" class="col-select">
            <mat-checkbox (click)="$event.stopPropagation()"
                          (change)="toggleSelectEmail(row)"
                          [checked]="isEmailSelected(row)"
                          color="primary">
            </mat-checkbox>
          </mat-cell>
        </ng-container>

        <ng-container matColumnDef="member_info">
          <mat-header-cell *matHeaderCellDef mat-sort-header> {{ 'generic.name' | translate }} / {{ 'generic.email' | translate }}</mat-header-cell>
          <mat-cell *matCellDef="let row">
            <div class="d-flex flex-column py-2">
              <span class="font-weight-bold text-dark">{{ row.name }}</span>
              <span class="text-muted small">{{ row.email }}</span>
            </div>
          </mat-cell>
        </ng-container>

        <ng-container matColumnDef="response_status">
          <mat-header-cell *matHeaderCellDef class="col-centered"> {{ 'generic.response' | translate }} </mat-header-cell>
          <mat-cell *matCellDef="let row" class="col-centered">
            <span class="status-pill"
                  [ngClass]="{
                    'status-sent': row.response_status === 'SENT',
                    'status-draft': row.response_status === 'DRAFT',
                    'status-none': row.response_status !== 'SENT' && row.response_status !== 'DRAFT'
                  }">
              {{ (row.response_status === 'SENT' ? 'generic.answers.yes' :
              (row.response_status === 'DRAFT' ? 'generic.answers.draft' : 'generic.answers.no')) | translate }}
            </span>
          </mat-cell>
        </ng-container>

        <ng-container matColumnDef="email_status">
          <mat-header-cell *matHeaderCellDef class="col-centered"> Email </mat-header-cell>
          <mat-cell *matCellDef="let row" class="col-centered">
            <div class="email-status-indicator"
                 [matTooltip]="('generic.email-status.' + (row.last_status_invitation_email | replaceSpaces) + '.title') | translate"
                 [style.color]="emailColorByTeamMember(row)"
                 (click)="emailResponsesAvailable(row) ? showEmailResponses(row) : ''; $event.stopPropagation()"
                 [style.cursor]="emailResponsesAvailable(row) ? 'pointer' : 'default'">
              <fa-icon [icon]="faEnvelope" size="lg"></fa-icon>
            </div>
          </mat-cell>
        </ng-container>

        <ng-container matColumnDef="actions">
          <mat-header-cell *matHeaderCellDef class="col-actions col-centered"> Actions </mat-header-cell>

          <mat-cell *matCellDef="let row" class="col-actions col-end">

            <button mat-icon-button color="primary"
                    *ngIf="impersonable(row)"
                    (click)="viewSentQuestionnaire(row); $event.stopPropagation()"
                    matTooltip="View Response">
              <fa-icon [icon]="faEye"></fa-icon>
            </button>

            <button mat-icon-button class="text-primary"
                    *ngIf="row.invitation_link && !surveyIsClosed"
                    (click)="copyLink(row.invitation_link); $event.stopPropagation()"
                    matTooltip="Copy Link">
              <fa-icon [icon]="faLink"></fa-icon>
            </button>

            <button *ngIf="!surveyIsClosed" mat-icon-button [matMenuTriggerFor]="menu" (click)="$event.stopPropagation()" class="text-muted">
              <mat-icon>more_vert</mat-icon>
            </button>
            <mat-menu #menu="matMenu">
              <button mat-menu-item (click)="resendInvitation([row.id])">
                <fa-icon [icon]="faBullhorn" class="mr-2 text-primary"></fa-icon>
                <span>Resend Invite</span>
              </button>
              <button mat-menu-item (click)="deleteTeamMembers([row.id])" class="text-danger">
                <fa-icon [icon]="faTrash" class="mr-2"></fa-icon>
                <span>Remove</span>
              </button>
            </mat-menu>

          </mat-cell>
        </ng-container>

        <mat-header-row *matHeaderRowDef="displayedColumns"></mat-header-row>
        <mat-row *matRowDef="let row; columns: displayedColumns;"
                 [class.selected-row]="isEmailSelected(row)"
                 class="hover-row pointer-cursor"
                 (click)="toggleSelectEmail(row)"> </mat-row>

      </mat-table>

      <mat-paginator #paginator [pageSizeOptions]="[10, 25, 50]" showFirstLastButtons class="border-top"></mat-paginator>
    </div>
  </div>
</div>
